
import type { Application } from '@/types/database.types';
import { supabase } from '@/integrations/supabase/client';
import { VALID_FIELDS } from './save-constants';
import type { Json } from '@/integrations/supabase/types';

// Helper to filter out placeholder values for select fields
export const normalizeFormData = (data: Partial<Record<string, any>>) => {
  const result = { ...data };
  
  // List of placeholder values that should be converted to null
  const placeholderValues = [
    'select_state',
    'select_industry',
    'select_provider',
    'select_structure'
  ];
  
  // Convert placeholder values to null
  Object.keys(result).forEach(key => {
    if (typeof result[key] === 'string' && placeholderValues.includes(result[key])) {
      result[key] = null;
    }
  });
  
  return result;
};

export const prepareUpdateData = (
  updateData: Partial<Record<string, any>>,
  existingData: Record<string, any>,
  type: 'application'
) => {
  console.log('Preparing update data for', type, updateData);
  
  // First normalize any placeholder values
  const normalizedData = normalizeFormData(updateData);

  const changes = Object.entries(normalizedData).filter(([key, value]) => {
    // Special handling for array and object fields that are stored as JSON
    if (key === 'additional_contacts' || key === 'health_plans') {
      return JSON.stringify(value) !== JSON.stringify(existingData[key]);
    }
    
    // Special handling for additional_schedules which is stored as jsonb[]
    if (key === 'additional_schedules') {
      // Deep comparison for array of objects
      return JSON.stringify(value) !== JSON.stringify(existingData[key]);
    }
    
    return value !== existingData[key];
  });

  const update = Object.fromEntries(changes);
  console.log('Changes detected:', update);
  return update;
};

/**
 * Converts application data to a format suitable for database storage
 * This ensures complex objects like AdditionalContact[] are converted to Json
 */
export const convertToDbFormat = (data: Partial<Application>): Record<string, any> => {
  const result: Record<string, any> = {};
  
  Object.entries(data).forEach(([key, value]) => {
    // Handle arrays of objects that need to be stored as JSONB
    if (key === 'additional_contacts' || key === 'health_plans') {
      // Ensure we're sending proper JSONB for these fields
      result[key] = value as Json;
    }
    // Handle special case for additional_schedules which is stored as jsonb[]
    else if (key === 'additional_schedules') {
      // Ensure it's formatted as an array for the database
      result[key] = value as Json[];
    }
    else {
      result[key] = value;
    }
  });
  
  return result;
};

/**
 * Prepares data for saving to the database, ensuring it's in the correct format
 * @param application Current application state
 * @param updates New data to be saved
 * @returns Properly formatted data for database update
 */
export const prepareApplicationDataForSave = (
  application: Application,
  updates: Partial<Application>
): Record<string, any> => {
  // First normalize the data to convert placeholder values to null
  const normalizedData = normalizeFormData(updates);
  
  // Process special cases for specific fields
  const processedUpdates = { ...normalizedData };
  
  // Apply database-friendly changes to the update data
  const updatesToSave = prepareUpdateData(processedUpdates, application, 'application');
  
  // Make sure we're only saving valid fields to the database
  const validUpdates: Record<string, any> = {};
  Object.keys(updatesToSave).forEach(key => {
    if (VALID_FIELDS.includes(key)) {
      validUpdates[key] = updatesToSave[key];
    } else {
      console.warn(`Field ${key} is not in the allowed list of fields and will not be saved.`);
    }
  });
  
  // Convert to database-compatible format
  return convertToDbFormat(validUpdates);
};

export const saveApplicationData = async (
  application: Application | null,
  setApplication: (app: Application | null) => void,
  updates: Partial<Application>,
  calculateProgress: () => number
): Promise<void> => {
  if (!application) {
    console.error('No application to update.');
    return;
  }

  const preparedUpdates = prepareApplicationDataForSave(application, updates);

  if (Object.keys(preparedUpdates).length === 0) {
    console.log('No changes detected, skipping save.');
    return;
  }

  try {
    const { data, error } = await supabase
      .from('applications')
      .update(preparedUpdates)
      .eq('id', application.id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    // Optimistically update the local state
    setApplication({
      ...application,
      ...updates, // Use original updates to maintain proper types in the React state
      progress: calculateProgress(),
      updated_at: new Date().toISOString(),
    });

    console.log('Application updated successfully:', data);
  } catch (error) {
    console.error('Error updating application:', error);
    throw error;
  }
};
